import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { ADMIN_PAGE_SECTION } from '../adminLayout';
import { CONSOLE_ANSWER_COPY as ANSWER } from '../consoleCopy';
import { ConsoleAnswer, ConsoleHeader } from './ConsoleHeader';
import { ENVIRONMENT_LABELS } from '../environment';
import { AdminGeneralSkeleton } from './AdminSkeletons';
import type { OrganizationEnvironment } from '@/lib/api/generated/model';

import {
  useOrganization,
  useUpdateOrganization,
} from '../hooks/useOrganization';

/**
 * The organization's name and which deployment this is (spec 073 US3).
 *
 * The smallest section, and the one nothing else depends on.
 *
 * The environment is **descriptive only**. The service's own contract says so and says
 * why: behaviour that differs between environments belongs in configuration, which is
 * validated at startup and cannot be changed by anyone with access to an admin form.
 * This screen says the same thing in the reader's words, because a field labelled
 * "Production / Staging" with no explanation invites exactly the assumption the
 * contract is warning against.
 */
export function AdminGeneralPage() {
  const organization = useOrganization();
  const showPlaceholder = usePendingDisplay(organization.isPending);

  // The header is static and was already outside the branch; all that was missing was
  // for the pending case to stop returning before it (spec 079 FR-005).
  return (
    <section className={ADMIN_PAGE_SECTION}>
      <ConsoleHeader
        title="General"
        description="How this organization is named and labelled wherever the application identifies it."
        answer={
          organization.data ? (
            <ConsoleAnswer
              figure={organization.data.name}
              words={ANSWER.generalWords(
                ENVIRONMENT_LABELS[organization.data.environment],
              )}
            />
          ) : undefined
        }
      />
      {organization.isPending ? (
        showPlaceholder ? (
          <AdminGeneralSkeleton />
        ) : null
      ) : organization.data === undefined ? (
        <p className="text-muted-foreground text-sm">
          The organization&rsquo;s details could not be read just now.
          Refreshing may help.
        </p>
      ) : (
        <GeneralForm
          name={organization.data.name}
          environment={organization.data.environment}
        />
      )}
    </section>
  );
}

function GeneralForm({
  name,
  environment,
}: {
  name: string;
  environment: OrganizationEnvironment;
}) {
  const [draftName, setDraftName] = useState(name);
  const [draftEnvironment, setDraftEnvironment] = useState(environment);
  const [submitted, setSubmitted] = useState(false);
  const update = useUpdateOrganization();

  // FR-022. Whitespace only is empty: a name of three spaces renders as a blank heading
  // wherever the organization is identified, which is worse than refusing it here.
  const nameIsEmpty = draftName.trim() === '';
  const error = submitted && nameIsEmpty ? 'Enter a name.' : null;
  const unchanged = draftName === name && draftEnvironment === environment;

  return (
    <form
      className="border-border bg-card flex max-w-lg flex-col gap-4 rounded-lg border px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitted(true);
        if (nameIsEmpty) return;
        // Trimmed on the way out, so the stored name is the one that will be read
        // back — not one with edges that only show up in a heading.
        update.mutate({
          name: draftName.trim(),
          environment: draftEnvironment,
        });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="organization-name">Name</Label>
        <Input
          id="organization-name"
          value={draftName}
          onChange={(event) => {
            setDraftName(event.target.value);
            setSubmitted(false);
          }}
          aria-invalid={error !== null}
          aria-describedby={
            error === null ? undefined : 'organization-name-error'
          }
          maxLength={200}
        />
        {error === null ? null : (
          <p
            id="organization-name-error"
            role="alert"
            className="text-destructive text-xs"
          >
            {error}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="organization-environment">Environment</Label>
        <Select
          value={draftEnvironment}
          onValueChange={(value) => {
            setDraftEnvironment(value as OrganizationEnvironment);
          }}
        >
          <SelectTrigger
            id="organization-environment"
            className="w-48 max-md:w-full"
          >
            {/*
              A children function, not a bare `<SelectValue />` (spec 074 FR-006).
              Bare, Base UI renders the *stored* value, so this field read
              `production` where every other select in the application reads its
              label — the trap `StepInspector` documents and this page walked into.
            */}
            <SelectValue>
              {(value: OrganizationEnvironment) => ENVIRONMENT_LABELS[value]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ENVIRONMENT_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-xs">
          A label for whoever is reading the screen. Nothing in the application
          behaves differently because of it.
        </p>
      </div>

      {update.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {update.error.message}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          className="max-md:w-full"
          disabled={update.isPending || unchanged}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {update.isSuccess && unchanged ? (
          <span role="status" className="text-muted-foreground text-sm">
            Saved.
          </span>
        ) : null}
      </div>
    </form>
  );
}
